import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Compass,
  ArrowRight,
  Check,
  Users,
  QrCode,
  FileText,
  Wallet,
  Mail,
  ShieldCheck,
  BookOpen,
  Sparkles,
  Crown,
  Clock,
  TrendingUp,
  Award,
  Landmark,
  Building2,
  Quote,
  Star,
  Feather,
  Zap,
  ChevronRight,
  BarChart3,
} from "lucide-react";
import { PublicDemoDashboard } from "./PublicDemoDashboard";

/**
 * Página pública de recursos com demonstrações inteiramente fictícias.
 */
export const FeaturesPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("balaustre");

  useEffect(() => {
    document.title = "Recursos — Lojas Integradas";
  }, []);

  return (
    <div className="min-h-screen bg-[#080C14] text-[#F8FAFC] selection:bg-[#D4AF37] selection:text-slate-950 font-sans">
      {/* Grain overlay */}
      <div
        className="pointer-events-none fixed inset-0 z-0 opacity-[0.04] mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' /%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' /%3E%3C/svg%3E\")",
        }}
      />

      {/* Nav */}
      <nav className="relative z-10 border-b border-slate-800/60 backdrop-blur-md bg-[#080C14]/80 sticky top-0">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            data-testid="features-brand-link"
            onClick={() => navigate("/")}
            className="flex items-center gap-3 group"
          >
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#8B6914] flex items-center justify-center shadow-lg shadow-[#D4AF37]/20 group-hover:scale-105 transition-transform">
              <Compass className="w-5 h-5 text-slate-950" strokeWidth={2.5} />
            </div>
            <div className="text-left">
              <div className="text-sm font-serif font-bold text-white leading-tight">
                Lojas Integradas
              </div>
              <div className="text-[10px] tracking-widest uppercase text-[#D4AF37]">
                Gestão Maçônica
              </div>
            </div>
          </button>
          <div className="flex items-center gap-4">
            <button
              data-testid="features-nav-pricing"
              onClick={() => navigate("/pricing")}
              className="hidden sm:block text-sm text-slate-300 hover:text-[#D4AF37] transition-colors"
            >
              Planos
            </button>
            <button
              data-testid="features-nav-blog"
              onClick={() => navigate("/blog")}
              className="hidden sm:block text-sm text-slate-300 hover:text-[#D4AF37] transition-colors"
            >
              Blog
            </button>
            <button
              data-testid="features-nav-signup"
              onClick={() => navigate("/signup")}
              className="text-sm px-5 py-2 rounded-lg bg-[#D4AF37] hover:bg-[#C5A028] text-slate-950 font-bold uppercase tracking-wider transition-all"
            >
              Cadastrar Loja
            </button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative z-10 max-w-6xl mx-auto px-6 pt-20 pb-16">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/5 px-4 py-1.5 text-xs uppercase tracking-widest text-[#D4AF37] mb-6">
            <Sparkles className="w-3.5 h-3.5" /> Recursos & Estudo de Caso
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold text-white leading-[1.1]">
            Tudo o que uma Loja moderna
            <br />
            <span className="text-[#D4AF37]">precisa em um só lugar.</span>
          </h1>
          <p className="mt-6 text-lg text-slate-400 max-w-2xl mx-auto leading-relaxed">
            Do Balaústre com assinatura digital ao Dashboard do Plano Ouro.
            Substitua planilhas, WhatsApps soltos e papéis perdidos por uma plataforma
            construída por Maçons, para Maçons.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              data-testid="features-cta-signup"
              onClick={() => navigate("/signup")}
              className="w-full sm:w-auto px-8 py-3.5 bg-[#D4AF37] hover:bg-[#C5A028] text-slate-950 font-bold text-sm uppercase tracking-wider rounded-xl shadow-lg shadow-[#D4AF37]/25 transition-all inline-flex items-center justify-center gap-2"
            >
              Testar grátis <ArrowRight className="w-4 h-4" />
            </button>
            <button
              data-testid="features-cta-pricing"
              onClick={() => navigate("/pricing")}
              className="w-full sm:w-auto px-8 py-3.5 bg-slate-800 hover:bg-slate-700 text-slate-100 font-semibold text-sm uppercase tracking-wider rounded-xl transition-all"
            >
              Ver planos
            </button>
          </div>
        </div>

        {/* Hero demonstrativo — nenhum dado ou identidade de Loja real */}
        <div className="mt-16 relative">
          <div className="absolute inset-0 bg-gradient-to-t from-[#080C14] via-transparent to-transparent z-10 pointer-events-none rounded-2xl" />
          <div className="overflow-hidden border border-slate-800 bg-[#0D1422] p-5 shadow-[0_0_60px_-15px_rgba(212,175,55,0.35)]" data-testid="features-demo-dashboard">
            <PublicDemoDashboard />
          </div>
          <div className="absolute -top-3 left-6 z-20 bg-[#D4AF37] text-slate-950 text-[10px] uppercase tracking-widest font-bold px-3 py-1 rounded" data-testid="features-fictional-badge">
            Demonstração fictícia — sem identidade real
          </div>
        </div>
      </section>

      {/* Feature Sections Alternating */}
      <FeatureBlock
        eyebrow="Módulo 01 · Secretaria"
        icon={FileText}
        title="Balaústre pronto ao fim da sessão"
        description="Escreva o Balaústre durante os trabalhos com um editor limpo. O papel timbrado da Loja é aplicado automaticamente, com brasão, cabeçalho e assinatura digital do Secretário. Envie por e-mail para todo o Quadro em um clique."
        bullets={[
          "Editor com atalhos maçônicos e correção",
          "Papel timbrado personalizado por Loja",
          "Assinatura digital com hash SHA-256",
          "Histórico completo pesquisável",
        ]}
        mockup={<BalaustreMockup />}
        reverse={false}
      />

      <FeatureBlock
        eyebrow="Módulo 02 · Livro de Presenças"
        icon={QrCode}
        title="Check-in por QR Code em segundos"
        description="Cada sessão gera um QR único. O Irmão aponta o celular no templo, faz login rápido e o Chanceler recebe a presença em tempo real. Fim das listas de papel."
        bullets={[
          "QR expirável por sessão (não pode ser reutilizado)",
          "Suporte a visitantes de outras Lojas",
          "Relatório de assiduidade automático",
          "Alerta ao Chanceler quando irmão falta 3× seguidas",
        ]}
        mockup={<QRMockup />}
        reverse={true}
      />

      <FeatureBlock
        eyebrow="Módulo 03 · Tesouraria"
        icon={Wallet}
        title="Tesouraria transparente e auditável"
        description="Centros de custo, mensalidades por grau, capitações, extrato individual por Irmão e balancete automático. Todo lançamento gera trilha de auditoria. Nunca mais discutir R$ 25,00 na Ágape."
        bullets={[
          "Extrato individual com PIX-copia-cola",
          "Centros de custo com metas mensais",
          "Balancete pronto para a Comissão de Contas",
          "Descontos automáticos por pontualidade",
        ]}
        mockup={<TesourariaMockup />}
        reverse={false}
      />

      {/* Demonstração visual da página de planos */}
      <section className="relative z-10 py-24 border-t border-slate-800/60">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-bold mb-3">
              Módulo 04 · Landing Comercial da sua Potência
            </div>
            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-white leading-tight">
              Sua Potência também vende. <br />
              <span className="text-[#D4AF37]">Deixe a landing pronta.</span>
            </h2>
            <p className="mt-4 text-slate-400">
              Página de planos comercial nativa — pronta para vender assinaturas mensais e anuais
              das Lojas afiliadas via Stripe.
            </p>
          </div>
          <div className="rounded-2xl overflow-hidden border border-slate-800 shadow-[0_0_60px_-15px_rgba(212,175,55,0.3)]">
            <img
              src="/features/pricing.png"
              alt="Página de Planos"
              className="w-full block"
              data-testid="pricing-screenshot"
            />
          </div>
        </div>
      </section>

      {/* Real screenshot #3 — Grand Orient — OCULTO (plano vendido sob demanda apenas)
      <section className="relative z-10 py-24 border-t border-slate-800/60 bg-gradient-to-b from-purple-950/10 to-transparent">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest text-purple-300 font-bold mb-3">
              <Crown className="w-3 h-3" /> Plano Ouro
            </div>
            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-white leading-tight">
              Um painel para a
              <span className="text-purple-300"> Potência.</span>
            </h2>
            <p className="mt-4 text-slate-400">
              Todas as Lojas afiliadas em um único dashboard. KPIs consolidados, distribuição
              por plano, obreiros ativos e saldo financeiro em tempo real.
            </p>
          </div>
          <div className="rounded-2xl overflow-hidden border border-purple-900/40 shadow-[0_0_60px_-15px_rgba(168,85,247,0.35)]">
            <img
              src="/features/grand-orient.png"
              alt="Dashboard do Plano Ouro"
              className="w-full block"
              data-testid="grand-orient-screenshot"
            />
          </div>
        </div>
      </section>
      */}
      {/* Case Study */}
      <CaseStudy />

      {/* Feature Grid – tudo mais */}
      <section className="relative z-10 py-24 border-t border-slate-800/60">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-white leading-tight">
              E ainda tem <span className="text-[#D4AF37]">tudo isto</span>
            </h2>
            <p className="mt-4 text-slate-400 text-sm">
              Feito com o rigor litúrgico que a Ordem exige.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {EXTRA_FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <div
                  key={i}
                  data-testid={`extra-feature-${i}`}
                  className="rounded-2xl bg-[#0d1420]/60 border border-slate-800/60 p-6 hover:border-[#D4AF37]/30 hover:-translate-y-1 transition-all group"
                >
                  <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 text-[#D4AF37] flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Icon className="w-5 h-5" strokeWidth={2} />
                  </div>
                  <h3 className="mt-4 text-lg font-serif font-bold text-white">{f.title}</h3>
                  <p className="mt-2 text-sm text-slate-400 leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative z-10 py-24 border-t border-slate-800/60">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <Building2 className="w-14 h-14 text-[#D4AF37] mx-auto mb-6" strokeWidth={1.5} />
          <h2 className="text-3xl sm:text-5xl font-serif font-bold text-white leading-tight">
            Sua Loja em ordem.
            <br />
            <span className="text-[#D4AF37]">Comece grátis hoje.</span>
          </h2>
          <p className="mt-5 text-slate-400 max-w-lg mx-auto">
            30 dias para conhecer todos os recursos do plano Pro. Sem cartão. Cancele quando quiser.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              data-testid="final-cta-signup"
              onClick={() => navigate("/signup")}
              className="w-full sm:w-auto px-10 py-3.5 bg-[#D4AF37] hover:bg-[#C5A028] text-slate-950 font-bold text-sm uppercase tracking-wider rounded-xl shadow-lg shadow-[#D4AF37]/25 transition-all inline-flex items-center justify-center gap-2"
            >
              Cadastrar minha Loja <ArrowRight className="w-4 h-4" />
            </button>
            <button
              data-testid="final-cta-pricing"
              onClick={() => navigate("/pricing")}
              className="w-full sm:w-auto px-8 py-3.5 bg-slate-800 hover:bg-slate-700 text-slate-100 font-semibold text-sm uppercase tracking-wider rounded-xl transition-all"
            >
              Ver planos
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-slate-800/60 py-8 px-6 text-center text-xs text-slate-500">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <span>Lojas Integradas • Plataforma SaaS Maçônica</span>
          <span className="font-mono text-[#D4AF37]">
            Liberdade • Igualdade • Fraternidade
          </span>
        </div>
      </footer>
    </div>
  );
};

/* ==============================
 * FEATURE BLOCK (alternate layout)
 * ============================== */
const FeatureBlock = ({ eyebrow, icon: Icon, title, description, bullets, mockup, reverse }) => (
  <section className="relative z-10 py-24 border-t border-slate-800/60">
    <div className="max-w-6xl mx-auto px-6">
      <div
        className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
          reverse ? "lg:[&>*:first-child]:order-2" : ""
        }`}
      >
        {/* Text */}
        <div>
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#D4AF37] font-bold mb-4">
            <Icon className="w-3.5 h-3.5" /> {eyebrow}
          </div>
          <h2 className="text-3xl sm:text-4xl font-serif font-bold text-white leading-tight">
            {title}
          </h2>
          <p className="mt-5 text-slate-400 leading-relaxed">{description}</p>
          <ul className="mt-8 space-y-3">
            {bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-slate-300">
                <Check className="w-4 h-4 text-[#D4AF37] flex-shrink-0 mt-0.5" strokeWidth={3} />
                {b}
              </li>
            ))}
          </ul>
        </div>
        {/* Mockup */}
        <div>{mockup}</div>
      </div>
    </div>
  </section>
);

/* ==============================
 * MOCKUPS (Balaustre / QR / Tesouraria)
 * ============================== */
const BalaustreMockup = () => (
  <div className="relative">
    <div className="absolute -top-4 -left-4 w-24 h-24 rounded-full bg-[#D4AF37]/10 blur-2xl" />
    <div className="relative rounded-2xl bg-gradient-to-br from-[#0d1420] to-[#0a0f1a] border border-slate-800 p-6 shadow-2xl">
      <div className="flex items-center justify-between pb-4 border-b border-slate-800">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded bg-[#D4AF37]/20 flex items-center justify-center">
            <Feather className="w-4 h-4 text-[#D4AF37]" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Balaústre</div>
            <div className="text-sm font-serif font-bold text-white">Sessão nº 1023</div>
          </div>
        </div>
        <div className="text-[10px] uppercase tracking-widest text-emerald-400 bg-emerald-500/10 px-2 py-1 rounded">
          Assinado
        </div>
      </div>
      <div className="mt-4 space-y-2 text-xs text-slate-400 leading-relaxed font-serif">
        <p>
          <span className="text-[#D4AF37] font-bold">Aos vinte e três dias do mês de janeiro,</span> reuniu-se
          esta Aug∴ Resp∴ Loja Symb∴ em sua sede, à Rua …
        </p>
        <p>
          O V∴M∴ abriu os Trab∴ no <span className="text-[#D4AF37]">Grau de Aprendiz</span>, dispensando
          o rito preliminar…
        </p>
        <p className="text-slate-600">
          Aprovou-se, por unanimidade, a Ata da sessão anterior. Passou-se à leitura da correspondência…
        </p>
      </div>
      <div className="mt-5 flex items-center justify-between pt-4 border-t border-slate-800">
        <div className="text-[10px] font-mono text-slate-500">SHA-256 · 7f3a…c92e</div>
        <div className="flex gap-1.5">
          <button className="text-[10px] uppercase tracking-widest text-slate-400 bg-slate-800 hover:bg-slate-700 px-3 py-1.5 rounded">
            PDF
          </button>
          <button className="text-[10px] uppercase tracking-widest text-slate-950 bg-[#D4AF37] hover:bg-[#C5A028] px-3 py-1.5 rounded font-bold">
            Enviar Quadro
          </button>
        </div>
      </div>
    </div>
  </div>
);

const QRMockup = () => (
  <div className="relative">
    <div className="absolute -top-4 -right-4 w-24 h-24 rounded-full bg-emerald-500/10 blur-2xl" />
    <div className="relative rounded-2xl bg-gradient-to-br from-[#0d1420] to-[#0a0f1a] border border-slate-800 p-6 shadow-2xl">
      <div className="text-center pb-5 border-b border-slate-800">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">
          Check-in de Sessão
        </div>
        <div className="text-xl font-serif font-bold text-white">Sessão nº 1024</div>
        <div className="text-xs text-slate-500">23 · Jan · 6023 AV∴L∴</div>
      </div>
      <div className="mt-5 flex items-center justify-center">
        {/* Fake QR */}
        <div className="w-40 h-40 grid grid-cols-10 grid-rows-10 gap-0.5 p-3 bg-white rounded-lg">
          {[...Array(100)].map((_, i) => (
            <div
              key={i}
              className={
                [
                  0, 1, 2, 3, 4, 6, 7, 8, 9, 10, 14, 19, 20, 24, 29, 30, 32, 34, 39,
                  40, 42, 44, 46, 49, 50, 52, 54, 56, 59, 65, 70, 71, 72, 73, 74, 75,
                  80, 84, 88, 89, 90, 94, 95, 99,
                ].includes(i)
                  ? "bg-slate-900"
                  : "bg-white"
              }
            />
          ))}
        </div>
      </div>
      <div className="mt-5 text-center">
        <div className="text-[10px] uppercase tracking-widest text-emerald-400 font-bold">
          ● Aguardando 14 obreiros
        </div>
        <div className="mt-2 flex justify-center -space-x-2">
          {["R", "F", "C", "M", "A"].map((l, i) => (
            <div
              key={i}
              className="w-7 h-7 rounded-full bg-[#D4AF37] text-slate-950 flex items-center justify-center text-[10px] font-bold border-2 border-[#0d1420]"
            >
              {l}
            </div>
          ))}
          <div className="w-7 h-7 rounded-full bg-slate-800 text-slate-400 flex items-center justify-center text-[10px] font-bold border-2 border-[#0d1420]">
            +9
          </div>
        </div>
      </div>
    </div>
  </div>
);

const TesourariaMockup = () => (
  <div className="relative">
    <div className="absolute -bottom-4 -left-4 w-24 h-24 rounded-full bg-cyan-500/10 blur-2xl" />
    <div className="relative rounded-2xl bg-gradient-to-br from-[#0d1420] to-[#0a0f1a] border border-slate-800 p-6 shadow-2xl">
      <div className="flex items-center justify-between pb-4 border-b border-slate-800">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
            Extrato · Ir∴ Ricardo M.
          </div>
          <div className="text-sm font-serif font-bold text-white mt-1">Janeiro / 2026</div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-emerald-400">Saldo</div>
          <div className="text-lg font-mono font-bold text-emerald-300">R$ 435,00</div>
        </div>
      </div>
      <div className="mt-4 space-y-2">
        {[
          { label: "Mensalidade Mestre", val: -180, cat: "Débito" },
          { label: "Ágape Sessão Magna", val: -55, cat: "Débito" },
          { label: "Doação Beneficência", val: -30, cat: "Débito" },
          { label: "Desconto Pontualidade", val: 20, cat: "Crédito" },
        ].map((r, i) => (
          <div
            key={i}
            className="flex items-center justify-between py-2 border-b border-slate-800/60 text-xs"
          >
            <div className="flex items-center gap-2">
              <div
                className={`w-1.5 h-1.5 rounded-full ${
                  r.val > 0 ? "bg-emerald-400" : "bg-red-400"
                }`}
              />
              <span className="text-slate-300">{r.label}</span>
            </div>
            <span
              className={`font-mono font-bold ${
                r.val > 0 ? "text-emerald-300" : "text-slate-300"
              }`}
            >
              {r.val > 0 ? "+" : ""}
              R$ {Math.abs(r.val).toFixed(2).replace(".", ",")}
            </span>
          </div>
        ))}
      </div>
      <button className="mt-4 w-full bg-[#D4AF37]/10 hover:bg-[#D4AF37]/20 border border-[#D4AF37]/30 text-[#D4AF37] text-[10px] uppercase tracking-widest font-bold py-2 rounded-lg">
        Copiar PIX Copia-Cola
      </button>
    </div>
  </div>
);

/* ==============================
 * CASE STUDY
 * ============================== */
const CaseStudy = () => (
  <section
    className="relative z-10 py-24 border-t border-slate-800/60 bg-gradient-to-b from-[#0a0f1a] to-transparent"
    data-testid="case-study-section"
  >
    <div className="max-w-6xl mx-auto px-6">
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-start">
        {/* Left — brand */}
        <div className="lg:col-span-2">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#D4AF37] font-bold mb-4">
            <Award className="w-3.5 h-3.5" /> Estudo de Caso
          </div>
          <h2 className="text-3xl sm:text-4xl font-serif font-bold text-white leading-tight">
            Loja Fictícia Aurora nº 101
          </h2>
          <div className="mt-3 text-sm text-slate-400 space-y-1">
            <div className="flex items-center gap-2">
              <MapMarker /> Oriente Demonstrativo — UF
            </div>
            <div className="flex items-center gap-2">
              <Landmark className="w-3.5 h-3.5 text-[#D4AF37]" /> Fundada em 1987 · 68 Obreiros
            </div>
            <div className="flex items-center gap-2">
              <Zap className="w-3.5 h-3.5 text-[#D4AF37]" /> Migrou para Lojas Integradas em Set/2025
            </div>
          </div>

          <blockquote className="mt-8 relative">
            <Quote className="w-8 h-8 text-[#D4AF37]/40 absolute -top-2 -left-2" />
            <p className="pl-8 text-lg text-slate-200 leading-relaxed italic font-serif">
              “Antes, o balaústre saía uma semana depois da sessão. Hoje, os Irmãos já recebem a
              versão assinada antes do café. É outro nível de disciplina.”
            </p>
            <div className="pl-8 mt-4">
              <div className="text-sm font-semibold text-white">Venerável fictício</div>
              <div className="text-xs text-slate-500">
                Venerável Mestre · Gestão 2024–2026
              </div>
            </div>
          </blockquote>
        </div>

        {/* Right — metrics */}
        <div className="lg:col-span-3 grid grid-cols-2 gap-4">
          <MetricCard
            icon={Clock}
            value="8h"
            label="economizadas por mês na Secretaria"
            accent="text-[#D4AF37] bg-[#D4AF37]/10"
          />
          <MetricCard
            icon={TrendingUp}
            value="+34%"
            label="de presença em sessões após QR check-in"
            accent="text-emerald-300 bg-emerald-500/10"
          />
          <MetricCard
            icon={ShieldCheck}
            value="Zero"
            label="atas questionadas desde a implantação"
            accent="text-purple-300 bg-purple-500/10"
          />
          <MetricCard
            icon={Wallet}
            value="98%"
            label="das mensalidades pagas em dia"
            accent="text-cyan-300 bg-cyan-500/10"
          />
          <div className="col-span-2 rounded-2xl bg-[#0d1420] border border-slate-800 p-5">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-3">
              Antes × Depois
            </div>
            <div className="space-y-2 text-sm">
              <Compare
                before="Balaústre em Word, revisão manual, correções por WhatsApp"
                after="Balaústre com assinatura digital enviado 15 min após o encerramento"
              />
              <Compare
                before="Lista de presença em papel, contagem manual pela Chancelaria"
                after="QR expira em 3h, presença em tempo real, alerta a 3 faltas"
              />
              <Compare
                before="Planilha Excel para mensalidades, cobrança verbal"
                after="Extrato individual com PIX copia-cola por Irmão"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Stars */}
      <div className="mt-14 flex items-center justify-center gap-1 text-[#D4AF37]">
        {[...Array(5)].map((_, i) => (
          <Star key={i} className="w-5 h-5 fill-current" />
        ))}
        <span className="ml-3 text-sm text-slate-400 font-medium">
          4,9 / 5 média em 27 Lojas ativas
        </span>
      </div>
    </div>
  </section>
);

const MetricCard = ({ icon: Icon, value, label, accent }) => (
  <div className="rounded-2xl bg-[#0d1420] border border-slate-800 p-5 flex flex-col">
    <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${accent}`}>
      <Icon className="w-4 h-4" />
    </div>
    <div className="mt-4 text-3xl font-serif font-bold text-white leading-none">{value}</div>
    <div className="mt-2 text-xs text-slate-400 leading-tight">{label}</div>
  </div>
);

const Compare = ({ before, after }) => (
  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
    <div className="flex-1 text-slate-500 line-through text-xs">{before}</div>
    <ChevronRight className="w-4 h-4 text-[#D4AF37] flex-shrink-0" />
    <div className="flex-1 text-slate-100 text-xs font-medium">{after}</div>
  </div>
);

const MapMarker = () => (
  <svg className="w-3.5 h-3.5 text-[#D4AF37]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const EXTRA_FEATURES = [
  { icon: BookOpen, title: "Câmaras de Instrução", desc: "Agendamento, tratados e frequência dos catecúmenos por grau." },
  { icon: Users, title: "Quadro de Obreiros", desc: "Cadastro completo com foto, CIM, grau, cargo e data de iniciação." },
  { icon: Mail, title: "Convocações e Boletim", desc: "Envio programado por e-mail para todo o Quadro em um clique." },
  { icon: ShieldCheck, title: "Trilha de Auditoria", desc: "Toda ação sensível fica registrada. Transparência total." },
  { icon: BarChart3, title: "Relatórios & Balancetes", desc: "Balancete pronto para a Comissão de Contas. Exportação CSV/PDF." },
];

export default FeaturesPage;
